import type { ActivityItemPresenceDisplayProps } from './interfaces';
import groupChatIcon from 'Assets/images/group-chat.svg';
import removedUser from 'Assets/images/removed-user.svg';
import cx from 'classnames';
import { UserAvatar } from 'Components/shared/UserAvatar';
import { usePreferences } from 'Hooks/preferences/usePreferences';
import { MobXProviderContext, observer } from 'mobx-react';
import * as React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { Styled } from './index.styles';

/**
 * Named export is not an observer
 */
const ActivityItemPresenceDisplay: React.FC<
  ActivityItemPresenceDisplayProps
> = ({
  conversationGrouping,
  conversationId = '0',
  personId,
  personPbo,
  showUnread = false,
  phoneNumber,
}) => {
  const {
    contact: contactStore,
    ui: uiStore,
    person: personStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const unreadCounts = uiStore.conversationUnreadCounts.get(conversationId) ?? {
    unreadMentions: 0,
    unreadMessages: 0,
  };

  const {
    preferences: { directMentionsOnly },
  } = usePreferences();

  const getDisplayIcon = () => {
    if (!showUnread) {
      return (
        <Styled.Avatar>
          <img src={groupChatIcon} alt="group-icon" />
        </Styled.Avatar>
      );
    }

    const unreadNumber = directMentionsOnly
      ? unreadCounts.unreadMentions
      : unreadCounts.unreadMessages;

    if (unreadNumber === 0) {
      return (
        <Styled.Avatar>
          <img src={groupChatIcon} alt="group-icon" />
        </Styled.Avatar>
      );
    }

    const isOver99 = unreadNumber > 99;
    const displayNumber = isOver99 ? (
      <span>
        99<div className="superscript">+</div>
      </span>
    ) : (
      unreadNumber
    );

    return (
      <Styled.Avatar>
        <div className={cx('number-circle', { 'over-99': isOver99 })}>
          {displayNumber}
        </div>
        <img src={groupChatIcon} alt="group-icon" />
      </Styled.Avatar>
    );
  };

  const displayIcon = getDisplayIcon();

  if (personPbo) {
    return personPbo.case({
      pending: () => displayIcon,
      rejected: () =>
        conversationGrouping === 'Channel' && (
          <div className="removed-user">
            R U
            <img src={removedUser} className="presence-badge" />
          </div>
        ),
      fulfilled: (p) => {
        if (!p) {
          return displayIcon;
        }

        if (personId) {
          if (
            conversationGrouping !== 'Channel' &&
            conversationGrouping !== 'Group'
          ) {
            const presence = uiStore.selectPersonPresenceStatus(personId);
            return (
              <UserAvatar
                name={p.name}
                showUnread={showUnread}
                conversationId={conversationId}
                presence={presence}
                image={p.avatarUrl}
                isInactive={p.deleted}
                key={`action-panel-avatar-${p.id}-${presence?.state}`}
              />
            );
          } else {
            return (
              <div className="list-item-icon-wrapper position-relative">
                {displayIcon}
                {unreadCounts.unreadMentions > 0 &&
                  unreadCounts.unreadMessages > 0 && (
                    <Styled.Mention>@</Styled.Mention>
                  )}
              </div>
            );
          }
        } else {
          return (
            <UserAvatar
              name={p.name}
              presence={uiStore.selectPersonPresenceStatus('0')}
            />
          );
        }
      },
    });
  }

  let hasContactMatch = false;
  let displayName = '';
  let displayAvatar = '';

  const matchingContactsPbo =
    personStore.getAllContactsWithSameNum(phoneNumber);

  if (
    matchingContactsPbo?.state === 'fulfilled' &&
    conversationGrouping === 'OneOnOne'
  ) {
    const matchingContacts = matchingContactsPbo.value;

    if (matchingContacts.length > 0) {
      hasContactMatch = true;
      displayName = matchingContacts[0].name;
      displayAvatar = matchingContacts[0].avatarUrl;
    }
  }

  return (
    <div className="phone-user relative">
      {showUnread && unreadCounts.unreadMessages > 0 && (
        <div
          className={cx('number-circle', {
            'over-99': unreadCounts.unreadMessages > 99,
          })}
        >
          {unreadCounts.unreadMessages <= 99 ? (
            unreadCounts.unreadMessages
          ) : (
            <span>
              99<div className="superscript">+</div>
            </span>
          )}
        </div>
      )}
      {conversationGrouping === 'Group' ? (
        displayIcon
      ) : (
        <UserAvatar
          {...(hasContactMatch &&
            conversationGrouping === 'OneOnOne' && {
              image: displayAvatar,
              name: displayName,
            })}
        />
      )}
    </div>
  );
};

export default observer(ActivityItemPresenceDisplay);
